import Tooltip from '@mui/material/Tooltip';
import type { FC } from 'react';
import { Fragment, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import DotsLoader from 'components/DotsLoader/DotsLoader';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import type { NoopT } from 'declarations/types/common';
import { formatAmount } from 'helpers/locale';

import { mapAgeToColor } from '../../helpers';

import TicketsCardViewLoader from './components/TicketsCardViewLoader';
import {
  CardCellStyled,
  CardContentStyled,
  CardStyled,
  CardViewStyled,
  TextLabel,
  TextRoNumberStyled,
  TextValueStyled,
} from './TicketsCardView.styled';

export type TicketsCardViewPropsT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  isLoadingTickets: boolean;
  onSelectTicket: (id: string, payload: { roNumber: string; lineNumber: string | null }) => NoopT;
};

const TicketsCardView: FC<TicketsCardViewPropsT> = ({
  ticketsPages,
  isLoadingTickets,
  onSelectTicket,
}) => {
  const { isFetchingNextTicketsPage, isFetchingTickets, hasNextTicketsPage, fetchNextTicketsPage } =
    useTickets();
  const { ref, inView } = useInView();
  const shouldLoadMore = isFetchingTickets && isFetchingNextTicketsPage && hasNextTicketsPage;

  useEffect(() => {
    if (inView && hasNextTicketsPage) {
      fetchNextTicketsPage();
    }
  }, [fetchNextTicketsPage, hasNextTicketsPage, inView]);

  return (
    <>
      {isLoadingTickets && !isFetchingNextTicketsPage ? (
        <TicketsCardViewLoader />
      ) : (
        <CardViewStyled data-testid="tickets-cards">
          {ticketsPages?.map((page, index) => (
            <Fragment key={`page-${index}`}>
              {page.data?.map(
                ({
                  id,
                  roNumber,
                  age,
                  assigneeName,
                  lineNumber,
                  statusId,
                  categoryName,
                  subCategory,
                  requestSubCategoryName,
                  claimAge,
                  claimStatus,
                  amount,
                  advisorNumber,
                  techNumber,
                }) => (
                  <CardStyled
                    key={id}
                    onClick={onSelectTicket(id, {
                      roNumber,
                      lineNumber: lineNumber ?? null,
                    })}
                  >
                    <CardContentStyled>
                      <CardCellStyled $col={1} $row={1}>
                        <TextLabel>RO</TextLabel>
                        <TextRoNumberStyled variant="body3" color="primaryText">
                          {roNumber}
                        </TextRoNumberStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={2} $row={1}>
                        <TextLabel>Status</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {statusId ?? '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={3} $row={1}>
                        <TextLabel>Age</TextLabel>
                        <TextValueStyled variant="body3" color={mapAgeToColor(age)}>
                          {age}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={1} $row={2}>
                        <TextLabel>Line</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {lineNumber || '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={2} $row={2}>
                        <TextLabel>Assignee</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {assigneeName}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={3} $row={2}>
                        <TextLabel>Category</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {categoryName || '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={1} $row={3}>
                        <TextLabel>Claim Age</TextLabel>
                        <TextValueStyled
                          variant="body3"
                          color={claimAge ? mapAgeToColor(claimAge) : 'primaryText'}
                        >
                          {claimAge ?? '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={2} $row={3}>
                        <TextLabel>Subcategory</TextLabel>
                        <Tooltip
                          title={subCategory ?? requestSubCategoryName ?? '-'}
                          enterDelay={400}
                          arrow
                          placement="top-start"
                        >
                          <TextValueStyled variant="body3" color="primaryText">
                            {subCategory ?? requestSubCategoryName ?? '-'}
                          </TextValueStyled>
                        </Tooltip>
                      </CardCellStyled>
                      <CardCellStyled $col={3} $row={3}>
                        <TextLabel>Claim Status</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {claimStatus || '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={1} $row={4}>
                        <TextLabel>Amount</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {amount != null ? formatAmount(amount) : '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={2} $row={4}>
                        <TextLabel>Advisor</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {advisorNumber || '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                      <CardCellStyled $col={3} $row={4}>
                        <TextLabel>Tech</TextLabel>
                        <TextValueStyled variant="body3" color="primaryText">
                          {techNumber || '-'}
                        </TextValueStyled>
                      </CardCellStyled>
                    </CardContentStyled>
                  </CardStyled>
                )
              )}
            </Fragment>
          ))}
        </CardViewStyled>
      )}
      <span
        ref={ref}
        style={{
          display: 'block',
          width: '100%',
          height: '20px',
        }}
      />
      {shouldLoadMore && (
        <FlexContainer justify="around">
          <DotsLoader />
        </FlexContainer>
      )}
    </>
  );
};

export default TicketsCardView;
